/**
 * Orca v3 check pool — the persisted shapes of one check job: what it runs
 * ({@link V3CheckJobSpec}), where it stands ({@link V3CheckJobState}) and what it returned
 * ({@link V3CheckJobResult}), as stored in `orcaloops_v3.check_jobs`.
 *
 * A check job is general: a container command plus its inputs, returning a verdict and
 * evidence. Nothing here is code-specific, so browser or judge checks can use the same pool
 * later. `@fm/v3` re-exports these schemas without the `V3` prefix (`@fm/v3/checks`), the
 * same way `@fm/v3/model` re-exports `V3Loop` / `V3Run`: they live here because the jobs
 * table types its jsonb and state columns with them, and `@fm/db` cannot import `@fm/v3`.
 *
 * The jobs table issue adds the row model (`V3CheckJob`) to this file.
 *
 * @since 1.0.0
 */
import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * The most of a job's output its result keeps: the tail, like a check part's evidence today.
 *
 * @since 1.0.0
 */
export const V3_CHECK_OUTPUT_TAIL_CHARS = 2500;

/**
 * A job's key, unique within a plan: `lint`, `types:tsgo`, `test:api:2/4`, `merge:api`.
 * Lowercase letters, digits and `: . _ / -`, starting with a letter or digit, at most 128.
 *
 * @since 1.0.0
 */
export const V3CheckJobKey = Schema.String.pipe(
  Schema.pattern(/^[a-z0-9][a-z0-9:._/-]{0,127}$/),
  Schema.annotations({ identifier: "CheckJobKey" }),
);
export type V3CheckJobKey = typeof V3CheckJobKey.Type;

/**
 * The name of a file one job produces and a later job consumes, e.g. `coverage:api:2of4`.
 * Like a job key but without `/`: the name is a single file, never a path.
 *
 * @since 1.0.0
 */
export const V3CheckArtifactName = Schema.String.pipe(
  Schema.pattern(/^[a-z0-9][a-z0-9:._-]{0,127}$/),
  Schema.annotations({ identifier: "CheckArtifactName" }),
);
export type V3CheckArtifactName = typeof V3CheckArtifactName.Type;

/**
 * An environment variable name a job sets.
 *
 * @since 1.0.0
 */
export const V3CheckEnvName = Schema.String.pipe(
  Schema.pattern(/^[A-Za-z_][A-Za-z0-9_]*$/),
  Schema.annotations({ identifier: "CheckEnvName" }),
);

const isEnvName = Schema.is(V3CheckEnvName);

/**
 * The variables a job adds to its container's environment. A name that is not a
 * {@link V3CheckEnvName} fails the whole map (a record keyed by a pattern would drop it
 * silently instead).
 *
 * @since 1.0.0
 */
export const V3CheckEnv = Schema.Record({ key: Schema.String, value: Schema.String }).pipe(
  Schema.filter((env) =>
    Object.keys(env).every(isEnvName) || "env names are letters, digits and _, not starting with a digit"
  ),
  Schema.annotations({ identifier: "CheckEnv" }),
);
export type V3CheckEnv = typeof V3CheckEnv.Type;

/**
 * One job of a check plan.
 *
 * - `command` is an argv, run as is in the job's container (a shell only when the argv
 *   says `bash -c`), in a fresh checkout of the source.
 * - `needs` are the keys of jobs that must pass first.
 * - `produces` / `consumes` are artifact names: each produced file is uploaded when the
 *   job passes, and each consumed file is downloaded before it starts, from the job among
 *   its `needs` that produces it.
 * - `memoryMb` is the container's memory limit; absent, the box's default.
 * - `timeoutMinutes` stops the job, and records the stop as `environment`.
 * - `env` is added to the container's environment. It never carries a secret.
 * - `secrets` names secrets the job's container gets as env vars of the same names. Names
 *   only: a value never appears in a spec, a plan, a jobs row or a result.
 *
 * @since 1.0.0
 */
export const V3CheckJobSpec = Schema.Struct({
  key: V3CheckJobKey,
  command: Schema.NonEmptyArray(Schema.String),
  needs: Schema.Array(V3CheckJobKey),
  produces: Schema.Array(V3CheckArtifactName),
  consumes: Schema.Array(V3CheckArtifactName),
  memoryMb: Schema.optional(Schema.Int.pipe(Schema.positive())),
  timeoutMinutes: Schema.Int.pipe(Schema.positive()),
  env: Schema.optional(V3CheckEnv),
  secrets: Schema.optional(Schema.Array(V3CheckEnvName)),
}).annotations({ identifier: "CheckJobSpec" });
export type V3CheckJobSpec = typeof V3CheckJobSpec.Type;

/**
 * Where a job stands.
 *
 * - `queued` — waiting for a box (and for its `needs` to pass).
 * - `leased` — a box claimed it and holds its lease.
 * - `passed` / `failed` — the job ran and returned its verdict.
 * - `error` — no verdict: the box or the pool stopped it (a time limit, a lost lease after
 *   the last attempt, a need that did not pass). Its result carries the reason as
 *   `environment`.
 *
 * @since 1.0.0
 */
export const V3CheckJobState = Schema.Literal("queued", "leased", "passed", "failed", "error");
export type V3CheckJobState = typeof V3CheckJobState.Type;

/**
 * States after which a job never changes again.
 *
 * @since 1.0.0
 */
export const V3_TERMINAL_CHECK_JOB_STATES: ReadonlyArray<V3CheckJobState> = ["passed", "failed", "error"];

/**
 * A file a job uploaded.
 *
 * @since 1.0.0
 */
export const V3CheckArtifact = Schema.Struct({
  name: V3CheckArtifactName,
  bytes: Schema.Int.pipe(Schema.nonNegative()),
});
export type V3CheckArtifact = typeof V3CheckArtifact.Type;

/**
 * What a job returned.
 *
 * - `ms` is the container's wall time.
 * - `output` is the tail of its output, at most {@link V3_CHECK_OUTPUT_TAIL_CHARS}.
 * - `environment` is present when the box or the pool stopped the job rather than the job
 *   deciding (the plain reason, like a check part's evidence today: "stopped at the
 *   15-minute limit"). A result with `environment` is never a pass.
 * - `peakMb` is the container's peak memory, when the box measured it.
 * - `artifacts` lists what the job uploaded.
 *
 * @since 1.0.0
 */
export const V3CheckJobResult = Schema.Struct({
  pass: Schema.Boolean,
  ms: Schema.Number.pipe(Schema.nonNegative()),
  output: Schema.String.pipe(Schema.maxLength(V3_CHECK_OUTPUT_TAIL_CHARS)),
  environment: Schema.optional(Schema.String),
  peakMb: Schema.optional(Schema.Number.pipe(Schema.nonNegative())),
  artifacts: Schema.Array(V3CheckArtifact),
}).annotations({ identifier: "CheckJobResult" });
export type V3CheckJobResult = typeof V3CheckJobResult.Type;

/**
 * One row of `orcaloops_v3.check_jobs`.
 *
 * A row with a null `reusedFromJobId` is a leader and runs; a copy points at the leader
 * whose result it reuses and is never claimed. `needJobIds` are the ids of the jobs that
 * must pass first. `attempt`, `runnerId` and `leaseExpiresAt` fence the current claim.
 *
 * @since 1.0.0
 */
export class V3CheckJob extends Model.Class<V3CheckJob>("V3CheckJob")({
  id: Model.Generated(Schema.String),
  customerId: Schema.String,
  runId: Schema.String,
  key: V3CheckJobKey,
  spec: V3CheckJobSpec,
  needJobIds: Schema.Array(Schema.String),
  /** Jobs with the same cache key produce the same result, so a finished one is reused. */
  cacheKey: Schema.String,
  image: Schema.String,
  sourceRef: Schema.String,
  sha: Schema.String,
  requestedBy: Schema.String,
  state: V3CheckJobState,
  /** How many times a box claimed it. */
  attempt: Schema.Number,
  runnerId: Schema.NullOr(Schema.String),
  leaseExpiresAt: Schema.NullOr(Schema.DateTimeUtc),
  result: Schema.NullOr(V3CheckJobResult),
  reusedFromJobId: Schema.NullOr(Schema.String),
  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
  startedAt: Schema.NullOr(Schema.DateTimeUtc),
  finishedAt: Schema.NullOr(Schema.DateTimeUtc),
}) {}
